// Frontend service layer — all API calls go through here.

import { API_BASE_URL } from "./config";
import { stripHtml, getServiceExcerpt, stripServiceDescriptionLead } from "../lib/html";
import { resolveCertificateFile } from "../lib/certificateFile";
import { optimizeBannerImageUrl, optimizePageBannerImageUrl } from "../lib/image";
import { primaryContactNumber } from "../lib/utils";

const LS_APPLICATIONS = "ssa_applications";

const SERVICE_ICONS = [
  "Building2",
  "Wrench",
  "Droplets",
  "Layers",
  "GitMerge",
  "Syringe",
  "Box",
  "Anchor",
  "ShieldCheck",
];

const CAREER_ICONS = ["TrendingUp", "GraduationCap", "Heart", "Users", "Building2", "Briefcase"];

const SECTOR_ICON_RULES = [
  { pattern: /power/i, icon: "Zap" },
  { pattern: /highway|bridge/i, icon: "Route" },
  { pattern: /metro|transit/i, icon: "TrainFront" },
  { pattern: /oil|gas|refiner/i, icon: "Fuel" },
  { pattern: /hydro/i, icon: "Waves" },
  { pattern: /commercial|residential/i, icon: "Building" },
  { pattern: /industrial|plant/i, icon: "Factory" },
  { pattern: /defence|defense/i, icon: "Shield" },
];

const SECTOR_ICON_FALLBACK = ["Zap", "Route", "TrainFront", "Fuel", "Waves", "Building", "Factory", "Shield"];

let aboutRequest = null;
let servicesRequest = null;
let homeRequest = null;

function unwrapData(json) {
  if (json && typeof json === "object" && "data" in json) return json.data;
  return json;
}

async function fetchV1(path, options) {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, options);
  } catch {
    throw new Error("Unable to connect to the server. Please check your connection and try again.");
  }

  let json;
  try {
    json = await res.json();
  } catch {
    throw new Error("Received an invalid response from the server.");
  }

  if (!res.ok) {
    const message =
      json?.message ||
      (Array.isArray(json?.errors) ? json.errors.join(", ") : null) ||
      `Request failed (${res.status}). Please try again later.`;
    throw new Error(message);
  }

  return json;
}

function normalizeImageUrl(item) {
  if (!item) return null;
  if (typeof item === "string") return item.trim() || null;
  return (
    item.image_url?.trim() ||
    item.homepage_image_url?.trim() ||
    item.url?.trim() ||
    null
  );
}

function mapHomepageImages(about) {
  return (about?.homepage_image_url || [])
    .map(normalizeImageUrl)
    .filter(Boolean);
}

function mapProjectImages(about) {
  const images = about?.project_images;
  if (!images) return [];
  if (Array.isArray(images)) {
    return images.map(normalizeImageUrl).filter(Boolean);
  }
  return [];
}

function mapFounders(about) {
  const founders = (about?.founders || [])
    .map((item) => ({
      id: item.id,
      name: item.name?.trim() || "",
      designation: item.designation?.trim() || "",
      imageUrl: item.image_url?.trim() || null,
    }))
    .filter((item) => item.name);

  if (founders.length > 0) return founders;

  if (about?.founder) {
    return [
      {
        id: "legacy-founder",
        name: about.founder,
        designation: about.designation?.trim() || "",
        imageUrl: about.image_url?.trim() || null,
      },
    ];
  }

  return [];
}

function mapCompanyInfo(about) {
  const normalizeSocialUrl = (url) => {
    if (!url) return "";
    const value = String(url).trim();
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    return `https://${value}`;
  };

  return {
    name: about.name,
    shortName: about.short_name,
    tagline: about.tagline,
    founder: about.founder,
    founders: mapFounders(about),
    founded: about.founded,
    professionals: about.professionals,
    branches: about.our_client ?? about.branches,
    experience: about.experience,
    projects: about.projects,
    email: about.email,
    phone: about.phone,
    primaryPhone: primaryContactNumber(about.phone),
    mobile: about.mobile,
    address: about.address,
    fullAddress: about.full_address,
    timing: about.timing,
    designation: about.designation,
    headerTagline: about.header_tagline,
    headerDescription: about.header_descripation,
    title: about.title,
    subTitle: about.sub_title,
    mainTitle: about.main_title,
    sectionTagline: about.section_tagline,
    description: about.description,
    imageUrl: about.image_url,
    linkedinUrl: normalizeSocialUrl(about.linkedin_url || about.linkedin || about.linkedinUrl),
    facebookUrl: normalizeSocialUrl(about.facebook_url || about.facebook || about.facebookUrl),
    youtubeUrl: normalizeSocialUrl(about.youtube_url || about.youtube || about.youtubeUrl),
    instagramUrl: normalizeSocialUrl(about.instagram_url || about.instagram || about.instagramUrl),
    twitterXUrl: normalizeSocialUrl(about.twitter_x_url || about.twitter_url || about.twitter || about.twitterXUrl),
    introductionUrl: normalizeSocialUrl(about.introduction_url || about.introductionUrl),
    bannerImageUrl: about.banner_image_url?.trim() || null,
    homepageImageUrls: mapHomepageImages(about),
    projectImageUrls: mapProjectImages(about),
    brochureUrl: about.brochure_url?.trim() || null,
  };
}

const STAT_DEFINITIONS = [
  { key: "experience", label: "Years of Experience" },
  { key: "projects", label: "Projects Delivered" },
  { key: "professionals", label: "Skilled Professionals" },
  { key: "branches", label: "Our Clients" },
];

function mapStats(about) {
  return STAT_DEFINITIONS.map(({ key, label }) => {
    const raw =
      key === "branches" ? about.our_client ?? about.branches : about[key];
    return {
      key,
      label,
      value: raw != null && raw !== "" ? String(raw) : "",
    };
  }).filter((stat) => stat.value);
}

function clientNameFromUrl(url) {
  try {
    const filename = new URL(url).pathname.split("/").pop() || "";
    const base = filename.replace(/\.[^.]+$/, "");
    return base.replace(/[-_]/g, " ").trim() || "Client";
  } catch {
    return "Client";
  }
}

function mapClients(about) {
  const imageUrls = (about?.clients_images || [])
    .map(normalizeImageUrl)
    .filter(Boolean);

  if (imageUrls.length > 0) {
    return imageUrls.map((iconUrl, index) => ({
      id: `client-image-${index}`,
      name: clientNameFromUrl(iconUrl),
      iconUrl,
    }));
  }

  return (about?.clients_data || [])
    .map((c) => ({
      id: c.id,
      name: c.name,
      iconUrl: c.icon?.trim() || null,
    }))
    .filter((c) => c.iconUrl);
}

function mapMissions(about) {
  return (about.about_missions || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((m) => ({
      id: m.id,
      title: m.title,
      description: m.description,
    }));
}

function mapBulletPoints(about) {
  return (about?.about_bullet_points || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((item) => ({
      id: item.id,
      text: (item.title || item.description || "").trim(),
    }))
    .filter((item) => item.text);
}

function mapServiceBulletPoints(item) {
  return (item?.service_bullet_points || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((point) => ({
      id: point.id,
      text: (point.title || point.description || "").trim(),
    }))
    .filter((point) => point.text);
}

function mapServiceImages(item) {
  const bannerImage = item.banner_image_url?.trim() || null;
  const imageUrls = (item.image_urls || [])
    .map(normalizeImageUrl)
    .filter(Boolean);
  const fallbackImage = item.image_url?.trim() || null;
  const galleryImageUrls = Array.from(
    new Set([fallbackImage, ...imageUrls].filter(Boolean))
  ).filter((url) => url !== bannerImage);

  return {
    bannerImageUrl: bannerImage,
    image: bannerImage || galleryImageUrls[0] || null,
    imageUrls: galleryImageUrls,
  };
}

function mapServiceItem(item, index) {
  const description = item.description || "";
  const title = item.title || "";
  const short = getServiceExcerpt(description, title, 160);
  const { bannerImageUrl, image, imageUrls } = mapServiceImages(item);

  return {
    id: item.id,
    position: item.position ?? index + 1,
    icon: SERVICE_ICONS[(item.position - 1) % SERVICE_ICONS.length] || SERVICE_ICONS[index % SERVICE_ICONS.length],
    title: item.title,
    short,
    description,
    bannerImageUrl,
    image,
    imageUrls,
    bulletPoints: mapServiceBulletPoints(item),
  };
}

function mapServicesPage(data) {
  const items = (data.service_items || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map(mapServiceItem);

  return {
    headerTagline: data.header_tagline,
    headerTitle: data.header_title,
    headerText: data.header_text,
    mainTitle: data.main_title,
    sectionTagline: data.section_tagline,
    subText: data.sub_text,
    bannerImageUrl: data.banner_image_url?.trim() || null,
    items,
  };
}

function mapPageHeader(header) {
  if (!header) return null;
  return {
    title: header.header_title || "",
    tagline: header.header_tagline || "",
    subtitle:
      header.header_description ||
      header.header_descripation ||
      header.header_text ||
      "",
    bannerImageUrl: header.banner_image_url?.trim() || null,
  };
}

function parseListPageResponse(payload, { listKey, headerKeys = [] }) {
  if (Array.isArray(payload)) {
    return { header: null, items: payload };
  }

  if (!payload || typeof payload !== "object") {
    throw new Error("Received an invalid response from the server.");
  }

  const items = payload[listKey];
  if (!Array.isArray(items)) {
    throw new Error("Received an invalid response from the server.");
  }

  const headerRaw = headerKeys.map((key) => payload[key]).find(Boolean);
  return { header: mapPageHeader(headerRaw), items };
}

function mapBlog(post) {
  const description = post.description || "";
  return {
    id: String(post.id),
    title: post.title,
    excerpt: description,
    plainExcerpt: stripHtml(description),
    category: "Insights",
    author: "SSA Techno",
    date: post.created_at,
    readTime: "5 min read",
    image: post.image_url?.trim() || null,
    tags: [],
    content: [{ type: "html", html: description }],
  };
}

function mapCareerBenefit(item, index) {
  return {
    id: item.id,
    title: item.title,
    desc: item.description,
    icon: CAREER_ICONS[index % CAREER_ICONS.length],
  };
}

function mapCareerOpening(job) {
  return {
    id: job.id,
    title: job.title,
    department: job.department,
    location: job.location,
    type: job.employment_type,
    experience: job.experience,
    posted: job.posted_on,
    summary: job.description,
    responsibilities: [],
    requirements: [],
  };
}

function buildAboutPayload(raw) {
  if (!raw) return null;
  return {
    ...mapCompanyInfo(raw),
    stats: mapStats(raw),
    clients: mapClients(raw),
    missions: mapMissions(raw),
    bulletPoints: mapBulletPoints(raw),
    contactHeader: mapPageHeader(raw.header_contact),
  };
}

function resolveSectorIcon(name, index) {
  const match = SECTOR_ICON_RULES.find(({ pattern }) => pattern.test(name || ""));
  return match?.icon || SECTOR_ICON_FALLBACK[index % SECTOR_ICON_FALLBACK.length];
}

function mapSectorImages(item) {
  const images = item?.images || [];
  if (!Array.isArray(images)) return [];
  return images
    .map((img) => {
      if (typeof img === "string") return img.trim();
      return img?.url?.trim() || img?.image_url?.trim() || normalizeImageUrl(img) || "";
    })
    .filter(Boolean);
}

function mapSector(item, index) {
  return {
    id: item.id,
    name: item.name || "",
    icon: resolveSectorIcon(item.name, index),
    iconUrl: item.icon_url?.trim() || null,
    imageUrls: mapSectorImages(item),
  };
}

function mapReview(item) {
  return {
    id: item.id,
    quote: item.description || "",
    author: item.name || "",
    company: item.designation || "",
  };
}

function mapHomeData(data) {
  const about = buildAboutPayload(data.about);
  const servicesRaw = Array.isArray(data.services) ? data.services[0] : data.services;
  const services = servicesRaw ? mapServicesPage(servicesRaw) : null;
  const banners = sortByPosition(
    (data.banners || []).filter((b) => b.status !== false),
    {
      getPosition: (banner) => parsePosition(banner.position, Number.MAX_SAFE_INTEGER),
      getId: (banner) => banner.id || 0,
    }
  ).map((banner) => mapBanner(banner, { crop: true }));
  const sectors = (data.sectors || [])
    .filter((s) => s.status !== false)
    .sort((a, b) => (a.id || 0) - (b.id || 0))
    .map(mapSector);
  const reviews = (data.reviews || [])
    .filter((r) => r.status !== false)
    .map(mapReview);

  return { banners, about, services, sectors, reviews };
}

function mapBanner(banner, { crop = false } = {}) {
  const raw = banner.image_url?.trim() || null;
  const image = raw
    ? (crop ? optimizeBannerImageUrl(raw) : optimizePageBannerImageUrl(raw)) || raw
    : null;
  return {
    id: banner.id,
    title: banner.title,
    description: banner.description || "",
    image,
    eyebrow: "Quality. Service. Safety.",
    bannerType: banner.banner_type,
    position: parsePosition(banner.position, Number.MAX_SAFE_INTEGER),
  };
}

function mapProject(item) {
  const imageUrls = (item.image_urls || [])
    .map(normalizeImageUrl)
    .filter(Boolean);
  const fallbackImage = item.image_url?.trim() || null;
  const uniqueImageUrls = Array.from(new Set([fallbackImage, ...imageUrls].filter(Boolean)));
  const status = item.project_status === "on_going" ? "ongoing" : "completed";
  return {
    id: item.id,
    name: item.title,
    location: item.location || item.location_name || "",
    category: item.category || "Project",
    image: uniqueImageUrls[0] || null,
    imageUrls: uniqueImageUrls,
    description: item.description || "",
    status,
  };
}

function mapProjectsHeader(header) {
  const mapped = mapPageHeader(header);
  if (!mapped) return null;
  return {
    title: mapped.title || "Our Projects",
    tagline: mapped.tagline,
    subtitle: mapped.subtitle,
    bannerImage: mapped.bannerImageUrl,
  };
}

function parseProjectsResponse(json) {
  const payload = unwrapData(json);

  if (Array.isArray(payload)) {
    return { header: null, projects: payload };
  }

  if (payload && Array.isArray(payload.projects)) {
    const header = mapProjectsHeader(
      payload.header_project || payload.projects[0]?.header_project
    );
    return { header, projects: payload.projects };
  }

  throw new Error("Received an invalid response from the server.");
}

function mapCertification(item) {
  const { image, documentUrl, fileUrl, fileType } = resolveCertificateFile(item);

  return {
    id: item.id,
    title: item.title || item.name || "",
    issuer: item.issuer || "",
    year: item.year != null && item.year !== "" ? String(item.year) : "",
    category: item.category || "",
    description: item.description || "",
    iconUrl: item.icon_url?.trim() || null,
    image,
    documentUrl,
    fileUrl,
    fileType,
  };
}

function parsePosition(value, fallback = 0) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function sortByPosition(items, { getPosition, getId }) {
  return items.slice().sort((a, b) => {
    const positionDiff = getPosition(a) - getPosition(b);
    if (positionDiff !== 0) return positionDiff;
    return getId(a) - getId(b);
  });
}

function mapTeamMember(item) {
  const photo = item.photo?.trim() || item.image_url?.trim() || null;
  const sortOrder = parsePosition(item.position, 0);

  return {
    id: item.id,
    name: item.name || "",
    designation: item.designation || "",
    sortOrder,
    photo,
  };
}

/** GET /api/v1/abouts */
export async function getAbout() {
  if (aboutRequest) return aboutRequest;

  aboutRequest = (async () => {
    const json = await fetchV1("/abouts");
    const list = unwrapData(json);
    const raw = Array.isArray(list) ? list.find((a) => a.status !== false) || list[0] : list;
    if (!raw) throw new Error("No about data available.");
    return buildAboutPayload(raw);
  })();

  try {
    return await aboutRequest;
  } finally {
    aboutRequest = null;
  }
}

/** GET /api/v1/services */
export async function getServices() {
  if (servicesRequest) return servicesRequest;

  servicesRequest = (async () => {
    const json = await fetchV1("/services");
    const list = unwrapData(json);
    const raw = Array.isArray(list) ? list[0] : list;
    if (!raw) throw new Error("No services data available.");

    return mapServicesPage(raw);
  })();

  try {
    return await servicesRequest;
  } finally {
    servicesRequest = null;
  }
}

/** Resolve a single service item by id from the services page payload */
export function resolveServiceById(page, id) {
  if (!page?.items?.length || id == null) return null;
  const index = page.items.findIndex((s) => String(s.id) === String(id));
  if (index === -1) return null;

  return {
    ...page.items[index],
    index,
    related: page.items.filter((s) => String(s.id) !== String(id)),
    pageHeader: {
      title: page.headerTitle,
      subtitle: page.headerText,
      bannerImageUrl: page.bannerImageUrl,
    },
  };
}

export async function getServiceById(id) {
  const page = await getServices();
  return resolveServiceById(page, id);
}

/** GET /api/v1/home */
export async function getHome() {
  if (homeRequest) return homeRequest;

  homeRequest = (async () => {
    const json = await fetchV1("/home");
    const data = unwrapData(json);
    if (!data) throw new Error("No home data available.");
    return mapHomeData(data);
  })();

  try {
    return await homeRequest;
  } finally {
    homeRequest = null;
  }
}

/** GET /api/v1/blogs */
async function fetchBlogsPage() {
  const json = await fetchV1("/blogs");
  const { header, items } = parseListPageResponse(unwrapData(json), {
    listKey: "blogs",
    headerKeys: ["header_blog"],
  });
  return {
    header,
    blogs: items.filter((b) => b.status !== false).map(mapBlog),
  };
}

export async function getBlogs() {
  const { blogs } = await fetchBlogsPage();
  return blogs;
}

export async function getBlogsPage() {
  return fetchBlogsPage();
}

/** GET /api/v1/blogs/:id */
export async function getBlogById(id) {
  const json = await fetchV1(`/blogs/${id}`);
  const raw = unwrapData(json);
  if (!raw) throw new Error("Blog not found.");
  return mapBlog(raw);
}

/** GET /api/v1/careers */
async function fetchCareersPage() {
  const json = await fetchV1("/careers");
  const { header, items } = parseListPageResponse(unwrapData(json), {
    listKey: "careers",
    headerKeys: ["header_career"],
  });
  return {
    header,
    benefits: items.filter((c) => c.status !== false).map(mapCareerBenefit),
  };
}

export async function getCareerBenefits() {
  const { benefits } = await fetchCareersPage();
  return benefits;
}

export async function getCareersPage() {
  return fetchCareersPage();
}

/** GET /api/v1/banners?banner_type=home */
export async function getBanners(bannerType = "home") {
  const qs = bannerType ? `?banner_type=${encodeURIComponent(bannerType)}` : "";
  const json = await fetchV1(`/banners${qs}`);
  const list = unwrapData(json);
  if (!Array.isArray(list)) throw new Error("Invalid banners response.");
  return sortByPosition(
    list.filter((b) => b.status !== false),
    {
      getPosition: (banner) => parsePosition(banner.position, Number.MAX_SAFE_INTEGER),
      getId: (banner) => banner.id || 0,
    }
  ).map(mapBanner);
}

/** GET /api/v1/career_openings?department=... */
export async function getCareerOpenings(department) {
  const qs = department && department !== "All" ? `?department=${encodeURIComponent(department)}` : "";
  const json = await fetchV1(`/career_openings${qs}`);
  const list = unwrapData(json);
  if (!Array.isArray(list)) throw new Error("Invalid career openings response.");
  return list.filter((j) => j.status !== false).map(mapCareerOpening);
}

/** POST /api/v1/queries */
export async function submitContact({ name, email, phone, subject, message }) {
  const json = await fetchV1("/queries", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      full_name: name,
      email,
      phone_number: phone,
      subject,
      message,
    }),
  });
  return unwrapData(json);
}

/** POST /api/v1/job_applications */
export async function submitApplication({
  resumeFile,
  name,
  email,
  phone,
  experience,
  currentCompany,
  coverLetter,
  jobId,
  jobTitle,
}) {
  const numericJobId = Number(jobId);
  const hasOpening = Number.isFinite(numericJobId) && numericJobId > 0;

  if (!hasOpening) {
    const record = {
      name,
      email,
      phone,
      experience,
      currentCompany,
      coverLetter,
      jobId,
      jobTitle,
      resumeName: resumeFile?.name || "",
      id: crypto.randomUUID?.() || String(Date.now()),
      at: new Date().toISOString(),
    };
    const list = JSON.parse(localStorage.getItem(LS_APPLICATIONS) || "[]");
    list.push(record);
    localStorage.setItem(LS_APPLICATIONS, JSON.stringify(list));
    return record;
  }

  const fd = new FormData();
  fd.append("full_name", name ?? "");
  fd.append("email", email ?? "");
  fd.append("phone", phone ?? "");
  fd.append("career_opening_id", String(numericJobId));
  fd.append("total_experience", experience ?? "");
  fd.append("current_company", currentCompany ?? "");
  fd.append("cover_letter", coverLetter ?? "");
  if (resumeFile) fd.append("resume", resumeFile);

  const json = await fetchV1("/job_applications", { method: "POST", body: fd });
  return json?.id ? json : unwrapData(json);
}

/** GET /api/v1/projects */
async function fetchProjects(filter = "all") {
  const params = new URLSearchParams();
  if (filter === "ongoing") params.set("project_status", "on_going");
  if (filter === "completed") params.set("project_status", "completed");
  const qs = params.toString();
  const url = `/projects${qs ? `?${qs}` : ""}`;

  const json = await fetchV1(url);
  const { header, projects } = parseProjectsResponse(json);
  return {
    header,
    projects: projects.filter((p) => p.status !== false).map(mapProject),
  };
}

export async function getProjects(filter = "all") {
  const { projects } = await fetchProjects(filter);
  return projects;
}

export async function getProjectsPage(filter = "all") {
  return fetchProjects(filter);
}

function formatGalleryTypeLabel(galleryType) {
  if (!galleryType?.trim()) return "";
  return galleryType
    .trim()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function mapGalleryAlbum(album) {
  if (album.status === false) return null;

  const galleryType = album.gallery_type?.trim() || "";
  const galleryTypeLabel = formatGalleryTypeLabel(galleryType) || album.category?.trim() || "";
  const title = album.name?.trim() || album.title?.trim() || galleryTypeLabel || "Gallery";
  const images = Array.isArray(album.images) ? album.images : [];

  const imageUrls = images
    .map((img) => normalizeImageUrl(img))
    .filter(Boolean);

  const fallbackImage = album.image_url?.trim() || album.image?.trim() || album.url?.trim() || null;
  const uniqueImageUrls = Array.from(new Set([fallbackImage, ...imageUrls].filter(Boolean)));

  if (uniqueImageUrls.length === 0) return null;

  return {
    id: album.id,
    title,
    category: galleryTypeLabel,
    galleryType,
    description: album.description || "",
    date: album.date || "",
    image: uniqueImageUrls[0],
    imageUrls: uniqueImageUrls,
  };
}

function mapGalleryItems(payload) {
  const albums = Array.isArray(payload) ? payload : payload?.galleries || [];
  return albums.map(mapGalleryAlbum).filter(Boolean);
}

export function countGalleryImages(albums = []) {
  return albums.reduce((total, album) => total + (album.imageUrls?.length || 0), 0);
}

/** GET /api/v1/galleries */
async function fetchGalleryPage() {
  const json = await fetchV1("/galleries");
  const payload = unwrapData(json);
  const items = mapGalleryItems(payload);

  if (Array.isArray(payload)) {
    return { header: null, items };
  }
  if (payload && Array.isArray(payload.galleries)) {
    return {
      header: mapPageHeader(payload.header_gallery),
      items,
    };
  }
  if (items.length > 0) {
    return { header: null, items };
  }
  throw new Error("Invalid gallery response.");
}

export async function getGallery() {
  const { items } = await fetchGalleryPage();
  return items;
}

export async function getGalleryPage() {
  return fetchGalleryPage();
}

/** GET /api/v1/certificates */
async function fetchCertificationsPage() {
  const json = await fetchV1("/certificates");
  const payload = unwrapData(json);
  if (Array.isArray(payload)) {
    return {
      header: null,
      items: payload.filter((c) => c.status !== false).map(mapCertification),
    };
  }
  if (payload && Array.isArray(payload.certificates)) {
    return {
      header: mapPageHeader(payload.header_certificate),
      items: payload.certificates.filter((c) => c.status !== false).map(mapCertification),
    };
  }
  throw new Error("Invalid certificates response.");
}

export async function getCertifications() {
  const { items } = await fetchCertificationsPage();
  return items;
}

export async function getCertificationsPage() {
  return fetchCertificationsPage();
}

/** GET /api/v1/team_members */
export async function getTeamMembers() {
  const json = await fetchV1("/team_members");
  const list = unwrapData(json);
  if (!Array.isArray(list)) throw new Error("Invalid team members response.");
  return list
    .filter((m) => m.status !== false)
    .map(mapTeamMember)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/** POST /api/v1/certifications — no mock fallback */
export async function addCertification({ imageFile, documentFile, ...payload }) {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ""));
  if (imageFile) fd.append("image", imageFile);
  if (documentFile) fd.append("document", documentFile);
  const json = await fetchV1("/certifications", { method: "POST", body: fd });
  return unwrapData(json);
}
